import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useMatchRoute, useNavigate, useRouter, useRouterState } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Upload, Check, Palette, LogOut, Menu, ChevronDown, Moon, Sun, Monitor, Trash2, LayoutDashboard, Sparkles, UserRound, UserRoundSearch, Rocket, Trophy, Store, Settings, Users, Eye, CornerDownRight, Terminal, Clapperboard, KeyRound, FlaskConical, ClipboardList } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import {
  cn,
  AdgeLogo,
  Button,
  SearchableCombobox,
  type ComboboxOptionGroup,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Sheet,
  SheetClose,
  SheetContent,
  SheetTrigger,
  useTheme,
} from "@nexus/ui";
import { HEADER_TRIGGER_STATIC, headerTriggerClassName } from "@/components/layout/header-trigger";
import { SettingsModal } from "@/components/layout/SettingsModal";
import { TenantSelector } from "@/components/layout/TenantSelector";
import { TaskProgressIndicator } from "@/features/task-progress/components/TaskProgressIndicator";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { useTenantSwitch } from "@/features/auth/hooks/useTenantSwitch";
import { useRecentTenants } from "@/features/auth/hooks/useRecentTenants";
import { useSettingsModalStore } from "@/hooks/useSettingsModalStore";
import { isBetaFeaturePageSlug, pageSlugToRoute } from "@/features/auth/lib/page-routes";
import { betaFeatureChildSlug, betaFeatureNavLabel } from "@/components/layout/sidebar-nav";
import { isNavSlugVisibleForTenantType, NAV_GROUPS, PRIMARY_NAV_SLUG } from "@/components/layout/nav-catalog";
import type { TenantPermissions } from "@/features/auth/lib/api";
import { presignUserAvatarUpload, confirmUserAvatarUpload } from "@/features/auth/api/user-avatar-upload.api";
import { USER_AVATAR_ACCEPT, getUserAvatarMaxBytes, isAllowedUserAvatarType } from "@/features/auth/lib/user-avatar-upload-constraints";
import { putToSignedUrlWithProgress, stageMemoryPreflightUpload } from "@/features/preflight/api/uploads.api";
import { updateUser } from "@/features/identity-hub/api/identity-hub.api";
import { OrgLogoMark } from "@/features/identity-hub/components/dashboard/OrgLogoMark";
import { SuperTenantBadge } from "@/features/identity-hub/components/dashboard/SuperTenantBadge";
import {
  ORG_PICKER_ITEM_PLAIN_CLASS,
  ORG_PICKER_POPOVER_CLASS,
  OrgPickerOptionContent,
} from "@/features/identity-hub/components/dashboard/org-picker-shared";
import { deriveUserRole } from "@/lib/analytics-utils";
import { Sentry } from "@/lib/sentry";
import { toast } from "sonner";
import { resolveExploreNavSearch, type DashboardDateReturn } from "@/features/dashboard/lib/explore-search";
import { useDashboardFiltersStore } from "@/features/dashboard/stores/dashboardFilters.store";

// ── Nav data types ─────────────────────────────────────────────────────────────

type NavDirectItem = {
  type: "link";
  slug: string;
  to: string;
  labelKey: string;
  label: string;
  icon?: LucideIcon;
  noHighlight?: boolean;
};

type NavGroupItem = {
  type: "group";
  slug: string;
  labelKey: string;
  label: string;
  items: Array<{
    slug: string;
    to: string;
    labelKey: string;
    label: string;
    icon?: LucideIcon;
    descKey?: string;
    desc?: string;
  }>;
};

type NavItem = NavDirectItem | NavGroupItem;

// ── Static config ──────────────────────────────────────────────────────────────

const PAGE_LABEL_KEYS: Record<string, string> = {
  "dashboard": "nav.dashboard",
  "identity-hub": "nav.identityHub",
  "opportunities": "nav.opportunities",
  "persona-opportunities": "nav.personaOpportunities",
  "workspace": "nav.workspace",
  "admin": "nav.admin",
  "personas": "nav.personas",
  "pre-flight": "nav.preFlight",
  "post-flight": "nav.postFlight",
  "pre-flight-scripts": "nav.preFlightScripts",
  "marketplace": "nav.marketplace",
  "marketplace.admin": "nav.marketplaceAdmin",
  "marketplace.orders": "nav.marketplaceOrders",
  "marketplace.partner": "nav.marketplacePartner",
  "overview": "nav.overview",
  "drilldown": "nav.drilldown",
  "prompts": "nav.prompts",
  "public-api": "nav.publicApi",
  "script-delivery": "nav.scriptDelivery",
};

const PAGE_LABELS: Record<string, string> = {
  "dashboard": "Dashboard",
  "identity-hub": "Identity Hub",
  "opportunities": "Opportunities",
  "persona-opportunities": "Opportunity Hub",
  "workspace": "Legacy Workspace",
  "admin": "Admin",
  "personas": "Personas",
  "pre-flight": "Pre Flight",
  "post-flight": "Post-Flight",
  "pre-flight-scripts": "Pre Flight – Scripts",
  "marketplace": "Marketplace",
  "marketplace.admin": "Partner Catalog",
  "marketplace.orders": "Orders tracker",
  "marketplace.partner": "Production orders",
  "overview": "Overview",
  "drilldown": "Drilldown",
  "prompts": "Prompts",
  "public-api": "Public API",
  "script-delivery": "Delivery Statistics dashboard",
};

const PAGE_ICONS: Record<string, LucideIcon> = {
  "dashboard": LayoutDashboard,
  "opportunities": Sparkles,
  "persona-opportunities": UserRoundSearch,
  "workspace": Eye,
  "admin": Settings,
  "identity-hub": Users,
  "personas": UserRound,
  "pre-flight": Rocket,
  "post-flight": Trophy,
  "pre-flight-scripts": Clapperboard,
  "marketplace": Store,
  "marketplace.admin": Store,
  "marketplace.orders": Store,
  "marketplace.partner": Store,
  "overview": Eye,
  "drilldown": CornerDownRight,
  "prompts": Terminal,
  "public-api": KeyRound,
  "script-delivery": ClipboardList,
};

// Short supporting copy shown under each item in the mega-menu. Placeholder copy — refine per product voice.
const PAGE_DESCRIPTIONS: Record<string, string> = {
  "dashboard": "Key metrics and performance at a glance",
  "workspace": "Explore and inspect your live data",
  "drilldown": "Dive into segment-level detail",
  "overview": "High-level summary across accounts",
  "marketplace": "Browse partners and send scripts",
  "opportunities": "Generate and manage opportunities",
  "persona-opportunities": "Generate and manage persona-based and competitor-based opportunity sets",
  "personas": "Build and target audience segments",
  "pre-flight": "Final checks before you launch",
  "post-flight": "See the ads that are winning",
  "pre-flight-scripts": "Review a video against its script",
  "admin": "Workspace settings and configuration",
  "identity-hub": "Users, roles and permissions",
  "prompts": "Manage AI prompt templates",
  "public-api": "Manage API tokens for Public API integrations",
  "marketplace.admin": "Manage the partner catalog",
  "marketplace.orders": "Track fulfilment across client orders",
  "marketplace.partner": "Submit preflight links for assigned orders",
  "script-delivery": "Scripts delivered this month against each client's target",
};

const PAGE_DESC_KEYS: Record<string, string> = {
  "dashboard": "nav.desc.dashboard",
  "workspace": "nav.desc.workspace",
  "drilldown": "nav.desc.drilldown",
  "overview": "nav.desc.overview",
  "marketplace": "nav.desc.marketplace",
  "opportunities": "nav.desc.opportunities",
  "persona-opportunities": "nav.desc.personaOpportunities",
  "personas": "nav.desc.personas",
  "pre-flight": "nav.desc.preFlight",
  "post-flight": "nav.desc.postFlight",
  "pre-flight-scripts": "nav.desc.preFlightScripts",
  "admin": "nav.desc.admin",
  "identity-hub": "nav.desc.identityHub",
  "prompts": "nav.desc.prompts",
  "public-api": "nav.desc.publicApi",
  "marketplace.admin": "nav.desc.marketplaceAdmin",
  "marketplace.orders": "nav.desc.marketplaceOrders",
  "marketplace.partner": "nav.desc.marketplacePartner",
  "script-delivery": "nav.desc.scriptDelivery",
};

const ALL_NAV_ROUTES = [
  "/dashboard",
  ...NAV_GROUPS.flatMap((group) =>
    group.pages.map((page) => pageSlugToRoute(page)).filter((route): route is string => route != null),
  ),
];

const NAV_DROPDOWN_CLOSE_DELAY_MS = 150;
const NAV_DROPDOWN_WIDE_MIN_ITEMS = 4;
const NAV_DROPDOWN_WIDTH_WIDE = "w-[460px]";
const NAV_DROPDOWN_WIDTH_NARROW = "w-[272px]";
const NAV_DROPDOWN_SIDE_OFFSET = 16;
const TENANT_SWITCHER_HOVER_OPEN_DELAY_MS = 100;
const TENANT_SWITCHER_HOVER_CLOSE_DELAY_MS = 250;
// Shared between the popover's contentClassName and the invisible hover bridge below,
// so the bridge always spans the panel's actual width.
const TENANT_SWITCHER_PANEL_WIDTH_CLASS = "w-72";

const THEME_OPTIONS = [
  { value: "light", icon: Sun, labelKey: "theme.light" },
  { value: "dark", icon: Moon, labelKey: "theme.dark" },
  { value: "system", icon: Monitor, labelKey: "theme.system" },
] as const;

// ── Hooks ──────────────────────────────────────────────────────────────────────

function useNavItems(tenants: TenantPermissions[] | undefined): NavItem[] {
  const activeTenantId = useAuth((s) => s.activeTenantId);
  return useMemo(() => {
    const activeTenant = tenants?.find((t) => t.id === activeTenantId) ?? tenants?.[0];
    const permissions = activeTenant?.permissions ?? [];
    const permittedSlugs = new Set<string>();
    for (const perm of permissions) {
      if (perm.type === "PAGE") permittedSlugs.add(perm.route);
    }
    // Navigation remains scoped to the active tenant. A SUPER_TENANT membership
    // elsewhere must not expose platform tooling while a customer org is active.

    // Partner Catalog requires list access to be meaningful — hide it without it.
    const canListVendors = permissions.some(
      (p) => p.method === "GET" && p.route === "/marketplace/admin/vendors",
    );
    if (!canListVendors) permittedSlugs.delete("marketplace.admin");

    const isPartnerTenant = activeTenant?.type === "PARTNER";
    if (isPartnerTenant) {
      const partnerItems: NavItem[] = [];
      if (permittedSlugs.has("marketplace.partner")) {
        partnerItems.push({
          type: "link",
          slug: "marketplace.partner",
          to: "/marketplace/partner",
          labelKey: "nav.marketplacePartner",
          label: "Production orders",
          icon: Store,
        });
      }
      return partnerItems;
    }

    const items: NavItem[] = [];

    if (permittedSlugs.has(PRIMARY_NAV_SLUG)) {
      items.push({
        type: "link",
        slug: PRIMARY_NAV_SLUG,
        to: "/dashboard",
        labelKey: "nav.dashboard",
        label: "Dashboard",
        icon: LayoutDashboard,
      });
    }

    for (const group of NAV_GROUPS) {
      const permitted = group.pages
        .filter((p) => p !== PRIMARY_NAV_SLUG)
        .filter((p) => permittedSlugs.has(p))
        .filter((p) => isNavSlugVisibleForTenantType(p, activeTenant?.type))
        .map((p) => ({
          slug: p,
          to: pageSlugToRoute(p)!,
          labelKey: PAGE_LABEL_KEYS[p] ?? p,
          label: PAGE_LABELS[p] ?? p,
          icon: PAGE_ICONS[p],
          descKey: PAGE_DESC_KEYS[p],
          desc: PAGE_DESCRIPTIONS[p],
        }));

      if (permitted.length > 0) {
        items.push({ type: "group", slug: group.slug, labelKey: group.labelKey, label: group.label, items: permitted });
      }
    }

    const betaSlugs = [...permittedSlugs]
      .filter(isBetaFeaturePageSlug)
      .sort((a, b) => betaFeatureChildSlug(a).localeCompare(betaFeatureChildSlug(b)));
    const betaItems = betaSlugs.flatMap((slug) => {
      const to = pageSlugToRoute(slug);
      if (!to) return [];
      return [{
        slug,
        to,
        labelKey: PAGE_LABEL_KEYS[slug] ?? `betaFeatures.nav.${betaFeatureChildSlug(slug)}`,
        label: PAGE_LABELS[slug] ?? betaFeatureNavLabel(slug),
        icon: PAGE_ICONS[slug] ?? FlaskConical,
        descKey: PAGE_DESC_KEYS[slug],
        desc: PAGE_DESCRIPTIONS[slug],
      }];
    });
    if (betaItems.length > 0) {
      items.push({
        type: "group",
        slug: "beta-features",
        labelKey: "nav.betaFeatures",
        label: "Beta Features",
        items: betaItems,
      });
    }

    return items;
  }, [tenants, activeTenantId]);
}

// ── Helpers ────────────────────────────────────────────────────────────────────

function deriveDisplayName(name?: string | null, email?: string): string {
  if (name) return name;
  if (!email) return "";
  const local = email.split("@")[0] ?? "";
  return local.charAt(0).toUpperCase() + local.slice(1);
}

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return ((parts[0]?.[0] ?? "") + (parts[parts.length - 1]?.[0] ?? "")).toUpperCase();
  return name.charAt(0).toUpperCase();
}

type MatchRouteFn = ReturnType<typeof useMatchRoute>;

function isNavRouteActive(matchRoute: MatchRouteFn, to: string): boolean {
  if (!matchRoute({ to: to as never, fuzzy: true })) return false;
  const hasMoreSpecificMatch = ALL_NAV_ROUTES.some(
    (other) =>
      other !== to &&
      other.startsWith(`${to}/`) &&
      matchRoute({ to: other as never, fuzzy: true }),
  );
  return !hasMoreSpecificMatch;
}

// ── TopNav ─────────────────────────────────────────────────────────────────────

interface TopNavProps {
  tenants?: TenantPermissions[];
}

export function TopNav({ tenants }: TopNavProps) {
  const { t } = useTranslation();
  const lastRouteSearch = useAuth((s) => s.lastRouteSearch);
  const dashboardDateReturn = useDashboardFiltersStore((s) => s.dashboardDateReturn);
  const activeTenantId = useAuth((s) => s.activeTenantId);
  const settingsOpen = useSettingsModalStore((s) => s.open);
  const setSettingsOpen = useSettingsModalStore((s) => s.setOpen);
  const navItems = useNavItems(tenants);

  const sortedTenants = useMemo(
    () => [...(tenants ?? [])].sort((a, b) => a.name.localeCompare(b.name)),
    [tenants],
  );

  const { record } = useRecentTenants(tenants ?? [], activeTenantId);
  const switchTenant = useTenantSwitch();

  const handleTenantSelect = (id: string) => {
    const tenant = sortedTenants.find((t) => t.id === id);
    if (tenant) record(tenant.id);
    switchTenant(id);
  };

  return (
    <>
      <header
        data-top-nav
        className="sticky top-0 z-40 flex h-14 shrink-0 items-center border-b border-[var(--border)] bg-[var(--background)] px-5 pb-px"
      >

        {/* ── Desktop ── */}
        <div className="hidden h-full flex-1 items-center gap-6 md:flex">
          <AdgeIcon className="text-[var(--foreground)]" />
          <nav className="flex h-full items-center gap-0.5" aria-label={t("nav.mainNavigation", "Main navigation")}>
            {navItems.map((item) =>
              item.type === "link" ? (
                <NavLinkItem
                  key={item.slug}
                  item={item}
                  lastRouteSearch={lastRouteSearch}
                  dashboardDateReturn={dashboardDateReturn}
                />
              ) : (
                <NavDropdownItem
                  key={item.slug}
                  item={item}
                  lastRouteSearch={lastRouteSearch}
                  dashboardDateReturn={dashboardDateReturn}
                />
              )
            )}
          </nav>
        </div>

        <div className="hidden shrink-0 items-center gap-2 md:flex" data-testid="desktop-actions">
          <TaskProgressIndicator />
          <TenantSwitcherTrigger
            sortedTenants={sortedTenants}
            activeTenantId={activeTenantId}
            onTenantSelect={handleTenantSelect}
            tenants={tenants}
          />
          <UserMenuTrigger tenants={tenants} />
        </div>

        {/* ── Mobile ── */}
        <div className="flex h-full flex-1 items-center gap-2 md:hidden">
          <MobileNavSheet
            navItems={navItems}
            tenants={tenants ?? []}
            activeTenantId={activeTenantId}
            onSelectTenant={handleTenantSelect}
            lastRouteSearch={lastRouteSearch}
            dashboardDateReturn={dashboardDateReturn}
          />
          <AdgeIcon className="text-[var(--foreground)]" />
        </div>

        <div className="flex shrink-0 items-center gap-2 md:hidden">
          <TaskProgressIndicator />
          <TenantSwitcherTrigger
            sortedTenants={sortedTenants}
            activeTenantId={activeTenantId}
            onTenantSelect={handleTenantSelect}
            tenants={tenants}
          />
          <UserMenuTrigger tenants={tenants} />
        </div>

      </header>

      <SettingsModal open={settingsOpen} onOpenChange={setSettingsOpen} />
    </>
  );
}

// ── NavLinkItem ────────────────────────────────────────────────────────────────

function NavLinkItem({
  item,
  lastRouteSearch,
  dashboardDateReturn,
}: {
  item: NavDirectItem;
  lastRouteSearch: Record<string, string>;
  dashboardDateReturn: DashboardDateReturn | null;
}) {
  const { t } = useTranslation();
  const matchRoute = useMatchRoute();
  const href = useRouterState({ select: (s) => s.location.href });
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const isActive = isNavRouteActive(matchRoute, item.to);
  const restoredSearch = resolveExploreNavSearch({
    targetPath: item.to,
    currentPath: pathname,
    href,
    lastRouteSearch,
    dashboardDateReturn,
  });
  const Icon = item.icon;

  return (
    <Link
      to={item.to as never}
      search={restoredSearch as never}
      className={headerTriggerClassName({
        selected: isActive && !item.noHighlight,
        className: Icon ? "gap-2" : undefined,
      })}
      aria-current={isActive ? "page" : undefined}
    >
      {Icon && <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden />}
      {t(item.labelKey, item.label)}
    </Link>
  );
}

// ── NavDropdownItem ────────────────────────────────────────────────────────────

function NavDropdownItem({
  item,
  lastRouteSearch,
  dashboardDateReturn,
}: {
  item: NavGroupItem;
  lastRouteSearch: Record<string, string>;
  dashboardDateReturn: DashboardDateReturn | null;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const matchRoute = useMatchRoute();
  const href = useRouterState({ select: (s) => s.location.href });
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const activeChild = item.items.find((child) => isNavRouteActive(matchRoute, child.to));
  const isAnyChildActive = !!activeChild;
  const isWide = item.items.length >= NAV_DROPDOWN_WIDE_MIN_ITEMS;

  const [open, setOpen] = useState(false);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Once the user clicks the trigger the menu is "pinned" open — hover-leave no longer closes it.
  const pinnedRef = useRef(false);
  // Marks an open-state change that originated from a trigger click (vs. escape / outside / select).
  const fromTriggerRef = useRef(false);

  const clearCloseTimer = () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    closeTimerRef.current = null;
  };

  const openMenu = () => {
    clearCloseTimer();
    setOpen(true);
  };

  const closeMenu = () => {
    if (pinnedRef.current) return;
    clearCloseTimer();
    closeTimerRef.current = setTimeout(() => {
      setOpen(false);
      closeTimerRef.current = null;
    }, NAV_DROPDOWN_CLOSE_DELAY_MS);
  };

  // Reconciles hover-open with click-to-pin. Radix toggles open on every trigger click,
  // so a click on a hover-opened menu would otherwise close it — here we pin it instead.
  const handleOpenChange = (next: boolean) => {
    clearCloseTimer();
    const fromTrigger = fromTriggerRef.current;
    fromTriggerRef.current = false;
    if (next) {
      if (fromTrigger) pinnedRef.current = true;
      setOpen(true);
      return;
    }
    if (fromTrigger && !pinnedRef.current) {
      pinnedRef.current = true;
      setOpen(true);
      return;
    }
    pinnedRef.current = false;
    setOpen(false);
  };

  useEffect(() => () => { if (closeTimerRef.current) clearTimeout(closeTimerRef.current); }, []);

  return (
    <div onMouseEnter={openMenu} onMouseLeave={closeMenu}>
      <DropdownMenu open={open} onOpenChange={handleOpenChange} modal={false}>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            onPointerDown={(e) => { if (e.button === 0) fromTriggerRef.current = true; }}
            className={headerTriggerClassName({ open, selected: isAnyChildActive })}
          >
            {t(item.labelKey, item.label)}
            {activeChild && (
              <>
                <span className="font-normal opacity-40" aria-hidden>·</span>
                <span className="font-normal">{t(activeChild.labelKey, activeChild.label)}</span>
              </>
            )}
            <ChevronDown
              className={cn("h-3 w-3 shrink-0 transition-transform duration-150", open && "rotate-180")}
              aria-hidden
            />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          sideOffset={NAV_DROPDOWN_SIDE_OFFSET}
          className={cn(
            "overflow-x-visible overflow-y-visible rounded-[12px] p-2",
            isWide ? NAV_DROPDOWN_WIDTH_WIDE : NAV_DROPDOWN_WIDTH_NARROW,
          )}
          onMouseEnter={openMenu}
          onMouseLeave={closeMenu}
        >
          {/* Bordered caret pointing up at the trigger button */}
          <span
            aria-hidden
            className="pointer-events-none absolute -top-1.5 left-5 h-3 w-3 rotate-45 rounded-tl-[3px] border-t border-l border-[var(--border)] bg-[var(--popover)]"
          />
          <div className={cn("grid gap-0.5", isWide && "grid-cols-2")}>
            {item.items.map((child) => {
              const isChildActive = isNavRouteActive(matchRoute, child.to);
              const restoredSearch = resolveExploreNavSearch({
                targetPath: child.to,
                currentPath: pathname,
                href,
                lastRouteSearch,
                dashboardDateReturn,
              });
              const ChildIcon = child.icon;
              return (
                <DropdownMenuItem
                  key={child.slug}
                  className={cn(
                    "group flex cursor-pointer items-start gap-3 rounded-[10px] p-2.5 transition-colors duration-150",
                    isChildActive
                      ? "bg-[var(--primary)]/5 focus:bg-[var(--primary)]/15"
                      : "focus:bg-[color-mix(in_srgb,var(--popover),var(--foreground)_4%)] dark:focus:bg-[color-mix(in_srgb,var(--popover),var(--foreground)_8%)]",
                  )}
                  aria-current={isChildActive ? "page" : undefined}
                  onSelect={() =>
                    void navigate({ to: child.to as never, search: restoredSearch as never })
                  }
                >
                  {ChildIcon && (
                    <span
                      className={cn(
                        "flex h-8 w-8 shrink-0 items-center justify-center rounded-[8px] border transition-colors duration-150",
                        isChildActive
                          ? "border-transparent bg-[var(--primary)]/10"
                          : "border-[var(--border)] bg-[var(--background)]",
                      )}
                      aria-hidden
                    >
                      <ChildIcon
                        className={cn(
                          "h-4 w-4 transition-all duration-200 ease-out group-data-[highlighted]:rotate-6 group-data-[highlighted]:scale-110 motion-reduce:transform-none",
                          isChildActive ? "text-[var(--primary-subtle-foreground)]" : "text-[var(--muted-foreground)]",
                        )}
                      />
                    </span>
                  )}
                  <span className="flex min-w-0 flex-col gap-0.5">
                    <span
                      className={cn(
                        "text-[13px] font-medium leading-tight tracking-[-0.01em]",
                        isChildActive ? "text-[var(--primary-subtle-foreground)]" : "text-[var(--foreground)]",
                      )}
                    >
                      {t(child.labelKey, child.label)}
                    </span>
                    {child.desc && (
                      <span className="line-clamp-2 text-[11px] leading-snug text-[var(--muted-foreground)]">
                        {child.descKey ? t(child.descKey, child.desc) : child.desc}
                      </span>
                    )}
                  </span>
                </DropdownMenuItem>
              );
            })}
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

// ── TenantSwitcherTrigger ──────────────────────────────────────────────────────

interface TenantSwitcherTriggerProps {
  sortedTenants: TenantPermissions[];
  activeTenantId: string | null;
  onTenantSelect: (id: string) => void;
  tenants: TenantPermissions[] | undefined;
}

function TenantSwitcherTrigger({
  sortedTenants,
  activeTenantId,
  onTenantSelect,
  tenants,
}: TenantSwitcherTriggerProps) {
  const { t } = useTranslation();
  const { recent } = useRecentTenants(tenants ?? [], activeTenantId);
  const activeTenant = sortedTenants.find((ten) => ten.id === activeTenantId) ?? sortedTenants[0];
  const hasMultipleTenants = sortedTenants.length > 1;

  const [open, setOpen] = useState(false);
  const openTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Once the user clicks the trigger the picker is "pinned" open — hover-leave no longer closes it.
  const pinnedRef = useRef(false);
  // Marks an open-state change that originated from a trigger click (vs. hover / select / outside).
  const fromTriggerRef = useRef(false);
  // True while focus is inside the panel (e.g. typing in the search box) — keeps it open
  // even if the pointer isn't currently over the trigger or panel.
  const focusWithinRef = useRef(false);

  const clearOpenTimer = () => {
    if (openTimerRef.current) clearTimeout(openTimerRef.current);
    openTimerRef.current = null;
  };
  const clearCloseTimer = () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    closeTimerRef.current = null;
  };

  const openMenu = () => {
    clearCloseTimer();
    if (open || openTimerRef.current) return;
    openTimerRef.current = setTimeout(() => {
      setOpen(true);
      openTimerRef.current = null;
    }, TENANT_SWITCHER_HOVER_OPEN_DELAY_MS);
  };

  const closeMenu = () => {
    clearOpenTimer();
    if (pinnedRef.current || focusWithinRef.current) return;
    clearCloseTimer();
    closeTimerRef.current = setTimeout(() => {
      setOpen(false);
      closeTimerRef.current = null;
    }, TENANT_SWITCHER_HOVER_CLOSE_DELAY_MS);
  };

  const handleMenuFocus = () => {
    focusWithinRef.current = true;
    clearCloseTimer();
  };
  const handleMenuBlur = () => {
    focusWithinRef.current = false;
    closeMenu();
  };

  // Reconciles hover-open with click-to-pin. Radix toggles the popover on every trigger
  // click, so a click on a hover-opened picker would otherwise close it — here we pin it instead.
  const handleOpenChange = (next: boolean) => {
    clearOpenTimer();
    clearCloseTimer();
    const fromTrigger = fromTriggerRef.current;
    fromTriggerRef.current = false;
    if (next) {
      if (fromTrigger) pinnedRef.current = true;
      setOpen(true);
      return;
    }
    if (fromTrigger && !pinnedRef.current) {
      pinnedRef.current = true;
      setOpen(true);
      return;
    }
    pinnedRef.current = false;
    focusWithinRef.current = false;
    setOpen(false);
  };

  useEffect(() => () => {
    if (openTimerRef.current) clearTimeout(openTimerRef.current);
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
  }, []);

  const groups = useMemo<ComboboxOptionGroup<TenantPermissions>[]>(() => {
    if (recent.length === 0) return [{ items: sortedTenants }];
    const recentIds = new Set(recent.map((r) => r.id));
    return [
      { heading: t("sidebar.recentTenants", "Recent"), items: recent },
      {
        heading: t("sidebar.allTenants", "All organizations"),
        items: sortedTenants.filter((ten) => !recentIds.has(ten.id)),
      },
    ];
  }, [recent, sortedTenants, t]);

  const pillInner = (open?: boolean) => (
    <>
      {activeTenant ? (
        <OrgLogoMark
          name={activeTenant.name}
          logoUrl={activeTenant.logoUrl}
          isSuperTenant={activeTenant.type === "SUPER_TENANT"}
          neutral
          size={16}
        />
      ) : (
        <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--primary)]" aria-hidden />
      )}
      <span className="truncate">
        {activeTenant?.name ?? t("sidebar.selectTenant", "Select organization")}
      </span>
      {hasMultipleTenants && (
        <ChevronDown
          className={cn(
            "h-3 w-3 shrink-0 transition-transform duration-150",
            open && "rotate-180",
          )}
          aria-hidden
        />
      )}
    </>
  );

  if (!hasMultipleTenants) {
    return (
      <div className={HEADER_TRIGGER_STATIC}>
        {pillInner()}
      </div>
    );
  }

  return (
    <div className="relative" onMouseEnter={openMenu} onMouseLeave={closeMenu}>
      <SearchableCombobox<TenantPermissions>
        groups={groups}
        value={activeTenantId}
        open={open}
        onOpenChange={handleOpenChange}
        onSelect={(tenant) => onTenantSelect(tenant.id)}
        getKey={(tenant) => tenant.id}
        getLabel={(tenant) => tenant.name}
        ariaLabel={t("sidebar.switchTenant", "Switch organization")}
        searchPlaceholder={t("sidebar.searchTenants", "Search organizations…")}
        emptyMessage={t("sidebar.noTenantsMatch", "No organizations match.")}
        align="end"
        contentClassName={cn(ORG_PICKER_POPOVER_CLASS, TENANT_SWITCHER_PANEL_WIDTH_CLASS, "duration-200")}
        itemClassName={ORG_PICKER_ITEM_PLAIN_CLASS}
        contentProps={{
          sideOffset: NAV_DROPDOWN_SIDE_OFFSET,
          onMouseEnter: openMenu,
          onMouseLeave: closeMenu,
          onFocusCapture: handleMenuFocus,
          onBlurCapture: handleMenuBlur,
          // A hover-open shouldn't yank keyboard focus into the search field as the
          // cursor passes over; only focus once the user pins it open with a click.
          onOpenAutoFocus: (event) => {
            if (!pinnedRef.current) event.preventDefault();
          },
        }}
        renderItem={(tenant) => {
          const role = deriveUserRole(tenant.permissions);
          return (
            <OrgPickerOptionContent
              org={tenant}
              showTypeBadge={false}
              accent={false}
              roleLabel={
                tenant.roleName ?? t(`sidebar.role.${role}`, { admin: "Admin", editor: "Editor", viewer: "Viewer" }[role] ?? role)
              }
            />
          );
        }}
        renderTrigger={({ open: triggerOpen }) => (
          <button
            type="button"
            role="combobox"
            aria-expanded={triggerOpen}
            aria-label={t("sidebar.switchTenant", "Switch organization")}
            // Radix Popover toggles on click (not pointerdown), so flag the
            // click here — it bubbles to Radix's toggle just before onOpenChange.
            onClick={() => { fromTriggerRef.current = true; }}
            className={headerTriggerClassName({ open: triggerOpen })}
          >
            {pillInner(triggerOpen)}
          </button>
        )}
      />
      {/* Invisible hover bridge spanning the trigger-to-panel gap (matches sideOffset)
          so the pointer never leaves tracked territory during trigger-to-panel transit. */}
      {open && (
        <span
          aria-hidden
          className={cn("pointer-events-auto absolute right-0 top-full", TENANT_SWITCHER_PANEL_WIDTH_CLASS)}
          style={{ height: NAV_DROPDOWN_SIDE_OFFSET }}
        />
      )}
    </div>
  );
}

// ── UserMenuTrigger ────────────────────────────────────────────────────────────

interface UserMenuTriggerProps {
  tenants: TenantPermissions[] | undefined;
}

function UserMenuTrigger({ tenants }: UserMenuTriggerProps) {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const updateUserPicture = useAuth((s) => s.updateUserPicture);
  const activeTenantId = useAuth((s) => s.activeTenantId);
  const router = useRouter();
  const { theme, setTheme } = useTheme();
  const [open, setOpen] = useState(false);
  const [avatarUploadProgress, setAvatarUploadProgress] = useState<number | null>(null);
  const [avatarPreviewUrl, setAvatarPreviewUrl] = useState<string | null>(null);
  const [photoPreviewOpen, setPhotoPreviewOpen] = useState(false);
  const [imgLoadError, setImgLoadError] = useState(false);
  const avatarInputRef = useRef<HTMLInputElement>(null);

  const currentTheme = THEME_OPTIONS.find((option) => option.value === theme) ?? THEME_OPTIONS[2];
  const ThemeIcon = currentTheme.icon;

  const displayName = deriveDisplayName(user?.name, user?.email);
  const userInitials = getInitials(displayName || "?");
  const role = (tenants ?? []).some((ten) =>
    ten.permissions.some((p) => p.type === "PAGE" && p.route === "identity-hub"),
  ) ? t("nav.roleAdmin", "Admin") : t("nav.roleMember", "Member");
  const isSuperTenant = (tenants ?? []).some((ten) => ten.type === "SUPER_TENANT");

  const isAvatarUploading = avatarUploadProgress !== null;
  const displayPhotoUrl = avatarPreviewUrl ?? user?.picture ?? null;
  // A custom photo may exist on the server yet fail to load locally (e.g. GCS URL),
  // so treat a load error as "no usable photo" for rendering and affordances.
  const showPhoto = Boolean(displayPhotoUrl) && !imgLoadError;

  useEffect(() => {
    return () => {
      if (avatarPreviewUrl) URL.revokeObjectURL(avatarPreviewUrl);
    };
  }, [avatarPreviewUrl]);

  useEffect(() => { setImgLoadError(false); }, [displayPhotoUrl]);

  const handleLogout = async () => {
    await logout();
    void router.navigate({ to: "/login" });
  };

  const handleAvatarFileChange = async (file: File | undefined) => {
    if (!file || !activeTenantId || !user) return;
    if (!isAllowedUserAvatarType(file.type)) {
      toast.error(t("nav.photoInvalidType", "Use PNG, JPEG, or WebP"));
      return;
    }
    const max = getUserAvatarMaxBytes(file.type);
    if (max !== undefined && file.size > max) {
      toast.error(t("nav.photoTooLarge", "Photo file is too large"));
      return;
    }

    if (avatarPreviewUrl) URL.revokeObjectURL(avatarPreviewUrl);
    const localUrl = URL.createObjectURL(file);
    setAvatarPreviewUrl(localUrl);
    setAvatarUploadProgress(0);

    try {
      const presign = await presignUserAvatarUpload({
        tenantId: activeTenantId,
        contentType: file.type,
        sizeBytes: file.size,
        originalName: file.name,
      });

      if (presign.requiresMemoryStage || presign.requiresServerStage) {
        await stageMemoryPreflightUpload({
          tenantId: activeTenantId,
          uploadId: presign.uploadId,
          contentType: file.type,
          file,
        });
      } else {
        await putToSignedUrlWithProgress(presign.uploadUrl, file, presign.requiredHeaders, {
          onProgress: setAvatarUploadProgress,
        });
      }

      const confirmed = await confirmUserAvatarUpload({
        tenantId: activeTenantId,
        uploadId: presign.uploadId,
      });

      updateUserPicture(confirmed.publicUrl ?? null);
      // Keep avatarPreviewUrl (blob URL) as the session display until page reload.
      // GCS URL in user.picture persists across sessions but may not load locally.

      updateUser({ userId: user.sub, tenantId: activeTenantId, changes: { avatarUploadId: presign.uploadId } })
        .catch((err: unknown) => Sentry.captureException(err));
    } catch (err) {
      URL.revokeObjectURL(localUrl);
      setAvatarPreviewUrl(null);
      toast.error(t("nav.photoUploadFailed", "Photo upload failed"));
      Sentry.captureException(err);
    } finally {
      setAvatarUploadProgress(null);
      if (avatarInputRef.current) avatarInputRef.current.value = "";
    }
  };

  const handleAvatarRemove = () => {
    if (avatarPreviewUrl) URL.revokeObjectURL(avatarPreviewUrl);
    setAvatarPreviewUrl(null);
    updateUserPicture(null);
    if (user && activeTenantId) {
      updateUser({ userId: user.sub, tenantId: activeTenantId, changes: { avatarUploadId: null } })
        .catch((err: unknown) => Sentry.captureException(err));
    }
  };

  return (
    <>
      <input
        ref={avatarInputRef}
        type="file"
        accept={USER_AVATAR_ACCEPT}
        className="sr-only"
        disabled={isAvatarUploading}
        onChange={(e) => void handleAvatarFileChange(e.target.files?.[0])}
      />
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={cn(
            "flex h-7 w-7 shrink-0 cursor-pointer items-center justify-center rounded-full bg-[var(--primary)] text-white outline-none overflow-hidden",
            "ring-2 ring-transparent transition-all duration-150",
            "hover:ring-[var(--primary)]/30 focus-visible:ring-[var(--ring)]",
          )}
          aria-label={t("nav.userMenu", "User menu")}
        >
          {displayPhotoUrl && !imgLoadError ? (
            <img src={displayPhotoUrl} alt={displayName ?? ""} className="h-full w-full object-cover" referrerPolicy="no-referrer" onError={() => setImgLoadError(true)} />
          ) : (
            <span className="text-[11px] font-medium leading-none select-none">{userInitials}</span>
          )}
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" sideOffset={6} className="w-64 overflow-hidden p-0">
        {/* ── Identity header ── */}
        <div className="flex items-center gap-3 bg-[color-mix(in_srgb,var(--muted),transparent_40%)] px-3 py-3">
          <div className="group relative h-10 w-10 shrink-0">
            <button
              type="button"
              className="relative flex h-full w-full cursor-pointer items-center justify-center overflow-hidden rounded-full bg-[var(--primary)] text-white outline-none transition-transform duration-150 hover:scale-[1.04] focus-visible:ring-2 focus-visible:ring-[var(--ring)] disabled:cursor-not-allowed motion-reduce:transform-none"
              aria-label={
                isAvatarUploading
                  ? t("nav.uploadingPhoto", "Uploading photo")
                  : showPhoto
                    ? t("nav.viewPhoto", "View profile photo")
                    : t("nav.uploadPhoto", "Upload photo")
              }
              disabled={isAvatarUploading}
              onClick={() => {
                if (showPhoto) {
                  setOpen(false);
                  setPhotoPreviewOpen(true);
                } else {
                  avatarInputRef.current?.click();
                }
              }}
            >
              {showPhoto ? (
                <img
                  src={displayPhotoUrl ?? undefined}
                  alt=""
                  className="h-full w-full object-cover"
                  referrerPolicy="no-referrer"
                  onError={() => setImgLoadError(true)}
                />
              ) : (
                <span className="text-sm font-semibold select-none">{userInitials}</span>
              )}
              {!showPhoto && !isAvatarUploading && (
                <span
                  aria-hidden
                  className="absolute inset-0 flex items-center justify-center bg-[var(--primary)]/70 text-white opacity-0 transition-opacity duration-150 group-hover:opacity-100 group-focus-within:opacity-100 motion-reduce:transition-none"
                >
                  <Upload className="h-4 w-4" />
                </span>
              )}
              {isAvatarUploading && (
                <span
                  aria-hidden
                  className="absolute inset-0 flex items-center justify-center bg-[var(--primary)]/75 text-white"
                >
                  <span className="text-[10px] font-medium tabular-nums">{avatarUploadProgress ?? 0}%</span>
                </span>
              )}
            </button>
            {showPhoto && !isAvatarUploading && (
              <button
                type="button"
                className="absolute -bottom-1 -right-1 flex h-[18px] w-[18px] scale-90 cursor-pointer items-center justify-center rounded-full bg-[var(--foreground)] text-[var(--background)] opacity-0 shadow-sm outline-none ring-2 ring-[var(--popover)] transition-[opacity,transform,background-color] duration-150 hover:bg-error hover:text-error-foreground focus-visible:scale-100 focus-visible:opacity-100 focus-visible:ring-[var(--ring)] group-hover:scale-100 group-hover:opacity-100 motion-reduce:transform-none"
                aria-label={t("nav.removePhoto", "Remove photo")}
                onClick={handleAvatarRemove}
              >
                <Trash2 className="h-2.5 w-2.5" />
              </button>
            )}
          </div>
          <div className="flex min-w-0 flex-1 flex-col">
            <span className="truncate text-[13px] font-semibold leading-tight text-[var(--foreground)]">
              {displayName}
            </span>
            <span className="truncate text-[11px] leading-tight text-[var(--muted-foreground)]">
              {user?.email}
            </span>
          </div>
          {/* Badges stack vertically so they fit beside the name without
              crowding the row in the narrow user-menu card. */}
          <div className="flex shrink-0 flex-col items-end gap-1">
            {isSuperTenant && <SuperTenantBadge size={14} />}
            <span className="rounded-full bg-[var(--primary)]/10 px-2 py-0.5 text-[10px] font-semibold tracking-wide text-[var(--primary-subtle-foreground)]">
              {role}
            </span>
          </div>
        </div>

        <DropdownMenuSeparator className="m-0" />

        {/* ── Actions ── */}
        <div className="p-1">
          <DropdownMenuSub>
            <DropdownMenuSubTrigger
              className={cn(
                "flex w-full cursor-pointer items-center gap-2.5 rounded-md px-2.5 py-2",
                "text-[13px] transition-colors duration-150",
                "hover:bg-[var(--muted)] focus:bg-[var(--muted)]",
                "data-[state=open]:bg-[var(--muted)]",
                "[&>svg:last-child]:hidden",
              )}
            >
              <Palette className="h-[14px] w-[14px] shrink-0 text-[var(--muted-foreground)]" aria-hidden />
              <span className="flex-1 truncate text-[var(--foreground)]">
                {t("settings.theme", "Theme")}
              </span>
              <div className="ml-auto flex items-center gap-1.5">
                <ThemeIcon className="h-3.5 w-3.5 text-[var(--muted-foreground)]" aria-hidden />
                <span className="text-[12px] text-[var(--muted-foreground)]">{t(currentTheme.labelKey)}</span>
                <ChevronDown className="h-3.5 w-3.5 -rotate-90 text-[var(--muted-foreground)]" aria-hidden />
              </div>
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent className="w-40 p-1" sideOffset={4} alignOffset={-4}>
              {THEME_OPTIONS.map((option) => {
                const OptionIcon = option.icon;
                return (
                  <DropdownMenuItem
                    key={option.value}
                    className="cursor-pointer gap-2.5 rounded-md px-2.5 py-2 text-[13px]"
                    onClick={() => setTheme(option.value)}
                  >
                    <OptionIcon className="h-[14px] w-[14px] shrink-0" aria-hidden />
                    <span className="flex-1">{t(option.labelKey)}</span>
                    <Check
                      aria-hidden
                      className={cn("h-3.5 w-3.5 text-[var(--primary)]", theme === option.value ? "opacity-100" : "opacity-0")}
                    />
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuSubContent>
          </DropdownMenuSub>
          <DropdownMenuItem
            className={cn(
              "cursor-pointer gap-2.5 rounded-md px-2.5 py-2 text-[13px] transition-colors duration-150",
              "text-[var(--muted-foreground)] hover:text-error focus:text-error",
              "hover:bg-error-subtle focus:bg-error-subtle",
            )}
            onClick={() => void handleLogout()}
          >
            <LogOut className="h-[14px] w-[14px] shrink-0 text-current" />
            {t("common.logout", "Log out")}
          </DropdownMenuItem>
        </div>
      </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={photoPreviewOpen} onOpenChange={setPhotoPreviewOpen}>
        <DialogContent className="sm:max-w-[360px]">
          <DialogHeader>
            <DialogTitle>{t("nav.profilePhoto", "Profile photo")}</DialogTitle>
          </DialogHeader>
          <div className="flex justify-center">
            {showPhoto && (
              <img
                src={displayPhotoUrl ?? undefined}
                alt={displayName}
                referrerPolicy="no-referrer"
                className="aspect-square w-56 rounded-2xl object-cover shadow-sm"
                onError={() => setImgLoadError(true)}
              />
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => avatarInputRef.current?.click()}>
              <Upload aria-hidden />
              {t("nav.changePhoto", "Change photo")}
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                handleAvatarRemove();
                setPhotoPreviewOpen(false);
              }}
            >
              <Trash2 aria-hidden />
              {t("nav.removePhoto", "Remove photo")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ── Mobile nav sheet ───────────────────────────────────────────────────────────

function MobileNavSheet({
  navItems,
  tenants,
  activeTenantId,
  onSelectTenant,
  lastRouteSearch,
  dashboardDateReturn,
}: {
  navItems: NavItem[];
  tenants: TenantPermissions[];
  activeTenantId: string | null;
  onSelectTenant: (id: string) => void;
  lastRouteSearch: Record<string, string>;
  dashboardDateReturn: DashboardDateReturn | null;
}) {
  const { t } = useTranslation();

  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 shrink-0 text-[var(--muted-foreground)]"
          aria-label={t("nav.openMenu", "Open navigation")}
        >
          <Menu className="h-4 w-4" />
        </Button>
      </SheetTrigger>
      <SheetContent side="left" className="w-72 p-0">
        <div className="flex h-14 items-center border-b border-[var(--border)] px-4">
          <AdgeLogo collapsed={false} className="text-[var(--foreground)]" />
        </div>
        <div className="flex flex-col gap-3 p-3">
          {tenants.length > 1 && (
            <TenantSelector
              tenants={tenants}
              activeTenantId={activeTenantId}
              onSelect={onSelectTenant}
              collapsed={false}
              onExpandRequest={() => {}}
            />
          )}
          <nav className="flex flex-col gap-0.5" aria-label={t("nav.mainNavigation", "Main navigation")}>
            {navItems.map((item) => {
              if (item.type === "link") {
                return (
                  <MobileNavLink
                    key={item.slug}
                    to={item.to}
                    label={t(item.labelKey, item.label)}
                    lastRouteSearch={lastRouteSearch}
                    dashboardDateReturn={dashboardDateReturn}
                  />
                );
              }
              return (
                <div key={item.slug} className="flex flex-col">
                  <p className="px-3 pb-0.5 pt-2 text-[11px] font-medium uppercase tracking-wider text-[var(--muted-foreground)]">
                    {t(item.labelKey, item.label)}
                  </p>
                  {item.items.map((child) => (
                    <MobileNavLink
                      key={child.slug}
                      to={child.to}
                      label={t(child.labelKey, child.label)}
                      lastRouteSearch={lastRouteSearch}
                      dashboardDateReturn={dashboardDateReturn}
                      indent
                    />
                  ))}
                </div>
              );
            })}
          </nav>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function MobileNavLink({
  to,
  label,
  lastRouteSearch,
  dashboardDateReturn,
  indent = false,
}: {
  to: string;
  label: string;
  lastRouteSearch: Record<string, string>;
  dashboardDateReturn: DashboardDateReturn | null;
  indent?: boolean;
}) {
  const matchRoute = useMatchRoute();
  const href = useRouterState({ select: (s) => s.location.href });
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const isActive = isNavRouteActive(matchRoute, to);
  const restoredSearch = resolveExploreNavSearch({
    targetPath: to,
    currentPath: pathname,
    href,
    lastRouteSearch,
    dashboardDateReturn,
  });

  return (
    <SheetClose asChild>
      <Link
        to={to as never}
        search={restoredSearch as never}
        className={cn(
          "flex cursor-pointer items-center rounded-[10px] px-3 py-2.5 text-sm font-medium transition-colors duration-150",
          indent && "pl-5",
          isActive
            ? "bg-[var(--primary)]/10 text-[var(--primary)] font-semibold"
            : "text-[var(--muted-foreground)] hover:bg-[var(--muted)] hover:text-[var(--foreground)]",
        )}
        aria-current={isActive ? "page" : undefined}
      >
        {label}
      </Link>
    </SheetClose>
  );
}

// ── AdgeIcon — chevron-only mark, theme-aware via fill="currentColor" ──────────

function AdgeIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="1.5 11 35.5 17.5"
      width={45}
      height={22}
      fill="currentColor"
      aria-label="Adge"
      className={className}
    >
      <path d="M35.86,11.26c-7.82,0-14.29,5.82-15.65,13.25h-.29c-1.36-7.42-7.83-13.25-15.65-13.25h-1.08v5.14h1.08c9.19,0,12.33,4.68,13.22,11.85h5.15c.89-7.17,4.03-11.85,13.22-11.85h1.08v-5.14h-1.08Z" />
    </svg>
  );
}
